import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Stethoscope, Home as HomeIcon, Building2, Quote } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function ShoutOuts() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [form, setForm] = useState({ nurse_name: "", setting: "hospital", hospital_name: "", message: "" });

  const load = () => api.get("/shoutouts").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.nurse_name.trim() || !form.message.trim()) return toast.error("Please add the nurse's name and a message");
    try {
      await api.post("/shoutouts", form);
      toast.success("Shout-out shared 💙 Thank you for honoring them!");
      setOpen(false);
      setForm({ nurse_name: "", setting: "hospital", hospital_name: "", message: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const shown = tab === "all" ? items : items.filter((i) => i.setting === tab);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl md:text-5xl text-[#1A202C] flex items-center gap-3">
            <Stethoscope className="text-[#6A0DAD]" /> Nurse Shout-Outs
          </h1>
          <p className="text-[#4A5568] mt-2">Honoring the nurses — at the hospital and at home — who care for our miracles.</p>
        </div>
        {user ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-shoutout-btn" className="inline-flex items-center gap-2 bg-[#6A0DAD] text-white hover:bg-[#58098F] rounded-full px-6 py-3 font-heading font-semibold transition-colors shadow-sm">
                <Plus size={18} /> Give a shout-out
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle className="font-heading">Honor a nurse</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="flex gap-2">
                  {[["hospital", "Hospital"], ["home", "Home"]].map(([k, label]) => (
                    <button key={k} data-testid={`shoutout-setting-${k}`} onClick={() => setForm({ ...form, setting: k })}
                      className={`flex-1 py-2 rounded-xl font-heading text-sm transition-colors ${form.setting === k ? "bg-[#6A0DAD] text-white" : "bg-white border border-[#6A0DAD] text-[#4A5568]"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <input data-testid="shoutout-nurse-input" placeholder="Nurse's name (e.g. Nurse Ana)" value={form.nurse_name} onChange={(e) => setForm({ ...form, nurse_name: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#58098F]" />
                <input data-testid="shoutout-hospital-input" placeholder="Hospital or agency (optional)" value={form.hospital_name} onChange={(e) => setForm({ ...form, hospital_name: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#58098F]" />
                <textarea data-testid="shoutout-message-input" placeholder="Share your gratitude…" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#58098F]" />
              </div>
              <DialogFooter>
                <button data-testid="shoutout-submit-btn" onClick={create} className="bg-[#6A0DAD] text-white hover:bg-[#58098F] rounded-full px-6 py-2.5 font-heading font-semibold transition-colors">Share</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : (
          <Link to="/auth" className="bg-[#6A0DAD] text-white rounded-full px-6 py-3 font-heading font-semibold">Sign in to shout out</Link>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        {[["all", "All"], ["hospital", "Hospital nurses"], ["home", "Home nurses"]].map(([k, label]) => (
          <button key={k} data-testid={`shoutout-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-1.5 rounded-full text-sm font-heading transition-colors ${tab === k ? "bg-[#6A0DAD] text-white" : "bg-white border border-[#6A0DAD] text-[#4A5568]"}`}>
            {label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="bg-white border border-[#6A0DAD] rounded-2xl p-12 text-center text-[#4A5568]">
          No shout-outs yet. Be the first to honor a nurse who made a difference.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((i) => (
            <div key={i._id} data-testid={`shoutout-card-${i._id}`} className="bg-white border border-[#6A0DAD] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all relative overflow-hidden">
              <Quote className="absolute -top-2 -right-2 text-[#6A0DAD]/10" size={72} />
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-heading font-semibold mb-3 ${i.setting === "hospital" ? "bg-[#6A0DAD]/10 text-[#6A0DAD]" : "bg-[#D4AF37]/12 text-[#B8972E]"}`}>
                {i.setting === "hospital" ? <Building2 size={13} /> : <HomeIcon size={13} />}
                {i.setting === "hospital" ? "Hospital" : "Home"} nurse
              </span>
              <h3 className="font-heading text-lg font-semibold text-[#1A202C]">{i.nurse_name}</h3>
              {i.hospital_name && <p className="text-xs text-[#4A5568] font-heading mb-2">{i.hospital_name}</p>}
              <p className="text-[#4A5568] leading-relaxed relative">{i.message}</p>
              <p className="text-xs text-[#4A5568] font-heading mt-4 pt-3 border-t border-[#6A0DAD]/20">— {i.author_name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
